import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { trainingFlowsApi } from '../../agents/services/trainingFlowsApi';
import { toast } from '@/hooks/use-toast';

interface CreateWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const resetForm = () => {
    setNombre('');
    setDescripcion('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!nombre.trim()) {
      toast.error('El nombre del flow es obligatorio');
      return;
    }

    setLoading(true);
    try {
      // Crear workflow sin número de teléfono ni agent_id
      const newFlow = await trainingFlowsApi.createEmptyFlow(
        nombre,
        descripcion
      );

      toast.success('Flow creado exitosamente');
      onSuccess?.();
      handleClose();
      
      // Navegar directamente al canvas del workflow
      navigate(`/workflows/${newFlow.id}`);
    } catch (error) {
      console.error('Error creating workflow:', error);
      toast.error('Error al crear el flow');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[20px] w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-grey-200">
          <h2 className="text-xl font-bold text-foreground font-montserrat">
            Crear Nuevo Flow
          </h2>
          <button
            onClick={handleClose}
            className="text-grey-400 hover:text-foreground transition-colors"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-grey-600 mb-4">
            Crea un flow para definir secuencias de mensajes, audios, imágenes y videos.
            Luego podrás conectarlo a un agente en "Mis Agentes".
          </p>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Nombre del Flow *
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Flujo de Bienvenida"
              className="w-full px-4 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Descripción (opcional)
            </label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Describe el propósito de este flow"
              rows={3}
              className="w-full px-4 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
              disabled={loading}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-grey-200">
          <button
            onClick={handleClose}
            className="flex-1 h-11 bg-grey-200 hover:bg-grey-300 text-foreground font-medium rounded-full transition-colors"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            className="flex-1 h-11 bg-primary hover:bg-primary/90 text-foreground font-medium rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !nombre.trim()}
          >
            {loading ? 'Creando...' : 'Crear y Editar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkflowModal;
